import { css, html, LitElement, nothing } from 'lit'
import { property, state } from 'lit/decorators.js'
import type { WorkCredit } from '@/components/WorkDetailCard'
import { minWidth } from '@/utilities/breakpoints'
import { safeCustomElement } from '@/utilities/custom-element'
import '@/components/Popover'
import '@/components/WorkDetailCard'
import type { MdcWorkThumb } from './WorkThumb'
import './WorkThumb'

export interface WorkDetail {
  src: string
  alt: string
  href: string
  cardImageSrc?: string
  cardTitle?: string
  campaignType?: string
  campaignName?: string
  pictureCredits?: WorkCredit[]
  workCredits?: WorkCredit[]
}

export const workGroupSizes = ['sm', 'md', 'lg'] as const
export type WorkGroupSize = (typeof workGroupSizes)[number]

@safeCustomElement('mdc-work-group')
export class MdcWorkGroup extends LitElement {
  @property({ attribute: false }) works: WorkDetail[] = []
  @property({ type: Number, attribute: 'max-visible' }) maxVisible = 3
  @property({ type: String, reflect: true }) size: WorkGroupSize = 'md'
  private _worksFromChildren = false

  @state() private _activeWork: WorkDetail | null = null
  @state() private _popoverAnchor: { x: number; y: number } | null = null
  @state() private _hoverWork: WorkDetail | null = null
  @state() private _hoverAnchor: { x: number; y: number } | null = null
  private _hoverTimeout: ReturnType<typeof setTimeout> | undefined
  private _desktopMql: MediaQueryList | undefined
  private _childObserver: MutationObserver | undefined
  private _onMqlChange = () => {
    if (!this._isDesktop()) this._dismissHover()
  }

  static styles = css`
    :host {
      display: inline-flex;
      --_wg-thumb-size: 3rem;
      --_wg-overlap: -0.625rem;
      --_wg-count-font: 0.75rem;
      --_wg-radius: 0.3125rem;
    }

    :host([size='sm']) {
      --_wg-thumb-size: 2.5rem;
      --_wg-overlap: -0.5rem;
      --_wg-count-font: 0.75rem;
      --_wg-radius: 0.25rem;
    }

    :host([size='lg']) {
      --_wg-thumb-size: 4.5rem;
      --_wg-overlap: -0.875rem;
      --_wg-count-font: 0.875rem;
      --_wg-radius: 0.375rem;
    }

    .mdc-work-group {
      display: inline-flex;
      align-items: center;
    }

    .mdc-work-group__thumb {
      display: block;
      width: var(--_wg-thumb-size);
      height: var(--_wg-thumb-size);
      border-radius: var(--_wg-radius);
      overflow: hidden;
      border: 2px solid var(--color-functional-gray-white, #fff);
      position: relative;
      transition: transform 0.15s ease;
    }

    .mdc-work-group__thumb:not(:first-child) {
      margin-left: var(--_wg-overlap);
    }

    .mdc-work-group__thumb:hover {
      transform: translateY(-2px);
      z-index: 10;
    }

    .mdc-work-group__thumb img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }

    .mdc-work-group__count {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: var(--_wg-thumb-size);
      height: var(--_wg-thumb-size);
      border-radius: var(--_wg-radius);
      background-color: var(--color-functional-gray-100, #f3f4f6);
      color: var(--color-functional-gray-600, #4b5563);
      font-family: var(--typography-font-family-sans);
      font-size: var(--_wg-count-font);
      font-weight: 500;
      margin-left: var(--_wg-overlap);
      border: 2px solid var(--color-functional-gray-white, #fff);
    }
  `

  connectedCallback() {
    super.connectedCallback()
    this._desktopMql = window.matchMedia(minWidth('md'))
    this._desktopMql.addEventListener('change', this._onMqlChange)

    // Observe child mdc-work-thumb elements for declarative usage
    this._childObserver = new MutationObserver(() => this._syncChildThumbs())
    this._childObserver.observe(this, { childList: true })
    this._syncChildThumbs()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this._desktopMql?.removeEventListener('change', this._onMqlChange)
    this._childObserver?.disconnect()
    clearTimeout(this._hoverTimeout)
  }

  /** Read <mdc-work-thumb> children and merge into works if present. */
  private _syncChildThumbs() {
    const thumbs = this.querySelectorAll<MdcWorkThumb>('mdc-work-thumb')
    if (thumbs.length === 0) return
    // Skip if works were set programmatically (not from children)
    if (this.works.length > 0 && !this._worksFromChildren) return
    this._worksFromChildren = true
    this.works = Array.from(thumbs).map((t) => t.toWorkDetail())
  }

  private _isDesktop() {
    return this._desktopMql?.matches ?? false
  }

  private _dismissHover() {
    this._hoverWork = null
    this._hoverAnchor = null
    clearTimeout(this._hoverTimeout)
  }

  private _scheduleDismissHover() {
    clearTimeout(this._hoverTimeout)
    this._hoverTimeout = setTimeout(() => this._dismissHover(), 200)
  }

  private _dismissPopover() {
    this._activeWork = null
    this._popoverAnchor = null
  }

  private _onThumbClick(e: MouseEvent, w: WorkDetail, i: number) {
    e.preventDefault()
    const anchor = e.currentTarget as HTMLElement
    const rect = anchor.getBoundingClientRect()
    const position = { x: rect.left, y: rect.bottom + 8 }

    if (this._activeWork === w) {
      this._activeWork = null
      this._popoverAnchor = null
      return
    }
    this._dismissHover()
    this._popoverAnchor = position
    this._activeWork = w

    this.dispatchEvent(
      new CustomEvent('work-click', {
        detail: { work: w, index: i, anchor, position },
        bubbles: true,
        composed: true,
      })
    )
  }

  private _onThumbHover(e: MouseEvent | FocusEvent, w: WorkDetail, i: number) {
    const anchor = e.currentTarget as HTMLElement
    const rect = anchor.getBoundingClientRect()
    const position = { x: rect.right + 8, y: rect.top }

    if (this._isDesktop()) {
      clearTimeout(this._hoverTimeout)
      this._hoverAnchor = position
      this._hoverWork = w
    }

    this.dispatchEvent(
      new CustomEvent('work-hover', {
        detail: { work: w, index: i, anchor, position },
        bubbles: true,
        composed: true,
      })
    )
  }

  private _onThumbLeave() {
    if (this._isDesktop()) {
      this._scheduleDismissHover()
    }

    this.dispatchEvent(
      new CustomEvent('work-hover-leave', {
        bubbles: true,
        composed: true,
      })
    )
  }

  private _onThumbKeydown(e: KeyboardEvent, w: WorkDetail, i: number) {
    if (e.key !== ' ') return
    e.preventDefault()
    const anchor = e.currentTarget as HTMLElement
    const rect = anchor.getBoundingClientRect()
    const position = { x: rect.left, y: rect.bottom + 8 }

    if (this._activeWork === w) {
      this._activeWork = null
      this._popoverAnchor = null
      return
    }
    this._dismissHover()
    this._popoverAnchor = position
    this._activeWork = w

    this.dispatchEvent(
      new CustomEvent('work-click', {
        detail: { work: w, index: i, anchor, position },
        bubbles: true,
        composed: true,
      })
    )
  }

  render() {
    if (!this.works?.length) return html``

    const visible = this.works.slice(0, this.maxVisible)
    const remaining = this.works.length - this.maxVisible

    return html`
      <div class="mdc-work-group">
        ${visible.map(
          (w, i) => html`
            <a
              class="mdc-work-group__thumb"
              href=${w.href}
              title=${w.alt}
              style="z-index: ${visible.length - i}"
              @click=${(e: MouseEvent) => this._onThumbClick(e, w, i)}
              @mouseenter=${(e: MouseEvent) => this._onThumbHover(e, w, i)}
              @focus=${(e: FocusEvent) => this._onThumbHover(e, w, i)}
              @mouseleave=${() => this._onThumbLeave()}
              @blur=${() => this._onThumbLeave()}
              @keydown=${(e: KeyboardEvent) => this._onThumbKeydown(e, w, i)}
            >
              <img src=${w.src} alt=${w.alt} loading="lazy" />
            </a>
          `
        )}
        ${remaining > 0 ? html`<span class="mdc-work-group__count">+${remaining}</span>` : ''}
      </div>
      <mdc-popover
        .open=${!!this._activeWork}
        .anchorX=${this._popoverAnchor?.x ?? 0}
        .anchorY=${this._popoverAnchor?.y ?? 0}
        @close=${this._dismissPopover}
      >
        ${
          this._activeWork
            ? html`<mdc-work-detail-card
                imageSrc=${this._activeWork.cardImageSrc ?? ''}
                imageAlt=${this._activeWork.alt ?? ''}
                cardTitle=${this._activeWork.cardTitle ?? ''}
                campaignType=${this._activeWork.campaignType ?? ''}
                campaignName=${this._activeWork.campaignName ?? ''}
                href=${this._activeWork.href}
                .pictureCredits=${this._activeWork.pictureCredits ?? []}
                .workCredits=${this._activeWork.workCredits ?? []}
              ></mdc-work-detail-card>`
            : nothing
        }
      </mdc-popover>
      <mdc-popover
        .open=${!!this._hoverWork}
        .anchorX=${this._hoverAnchor?.x ?? 0}
        .anchorY=${this._hoverAnchor?.y ?? 0}
        noBackdrop
        @close=${this._dismissHover}
      >
        ${
          this._hoverWork
            ? html`<mdc-work-detail-card
                imageSrc=${this._hoverWork.cardImageSrc ?? ''}
                imageAlt=${this._hoverWork.alt ?? ''}
                cardTitle=${this._hoverWork.cardTitle ?? ''}
                campaignType=${this._hoverWork.campaignType ?? ''}
                campaignName=${this._hoverWork.campaignName ?? ''}
                href=${this._hoverWork.href}
                .pictureCredits=${this._hoverWork.pictureCredits ?? []}
                .workCredits=${this._hoverWork.workCredits ?? []}
              ></mdc-work-detail-card>`
            : nothing
        }
      </mdc-popover>
    `
  }
}
